/**
 * Socket.IO client for real-time notifications.
 * Uses default path /socket.io. See docs/FRONTEND-SOCKET-IO-CORS.md for CORS and URL setup.
 */
import { io, Socket } from 'socket.io-client'

interface SocketCallbacks {
  onConnect?: () => void
  onDisconnect?: (reason: string) => void
  onError?: (error: Error) => void
  onReconnect?: (attemptNumber: number) => void
  onReconnectFailed?: () => void
  onNotification?: (notificationData: any) => void
  onReadConfirmed?: (data: { notificationId: string }) => void
}

class SocketService {
  private socket: Socket | null = null
  private isConnected: boolean = false
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 5

  /**
   * Connect to Socket.IO server (same host as REST API).
   * @param serverUrl - Same base URL as REST API (e.g. https://api.maarefati.com)
   * @param token - JWT (same token as REST; send new token after refresh to reconnect)
   * @param callbacks - Event callbacks
   */
  connect(serverUrl: string, token: string, callbacks: SocketCallbacks = {}): Socket | null {
    // Disconnect existing connection if any
    if (this.socket) {
      this.disconnect()
    }

    if (!serverUrl || !token) {
      console.warn('Socket.IO: Missing serverUrl or token')
      return null
    }

    this.socket = io(serverUrl, {
      auth: { token },
      transports: ['polling'], // or ['websocket', 'polling'] if WSS ready
      path: '/socket.io', // ensure it matches Nginx
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
      withCredentials: true,
    })

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true
      this.reconnectAttempts = 0
      if (callbacks.onConnect) callbacks.onConnect()
    })

    this.socket.on('disconnect', (reason: string) => {
      this.isConnected = false
      if (callbacks.onDisconnect) callbacks.onDisconnect(reason)
    })

    this.socket.on('connect_error', (error: Error) => {
      this.reconnectAttempts++
      // Do not log: WebSocket fails when backend does not serve Socket.IO (app works without it)
      if (callbacks.onError) callbacks.onError(error)
    })

    this.socket.on('reconnect_attempt', () => {
      // Avoid logging every attempt to reduce console spam when server is unreachable
    })

    this.socket.on('reconnect_failed', () => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Socket.IO reconnection failed')
      }
      if (callbacks.onReconnectFailed) callbacks.onReconnectFailed()
    })

    // Notification events
    this.socket.on('notification:new', (notificationData: any) => {
      if (callbacks.onNotification) callbacks.onNotification(notificationData)
    })

    this.socket.on('notification:read:confirmed', (data: { notificationId: string }) => {
      if (callbacks.onReadConfirmed) callbacks.onReadConfirmed(data)
    })

    return this.socket
  }

  /**
   * Disconnect from server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
    }
  }

  /**
   * Mark notification as read
   * @param notificationId - Notification ID
   */
  markNotificationAsRead(notificationId: string): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('notification:read', { notificationId })
    }
  }

  /**
   * Check if connected
   */
  getConnectionStatus(): { connected: boolean; socketId: string | null } {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id || null,
    }
  }

  /**
   * Get raw socket instance (e.g. for custom events).
   */
  getSocket(): Socket | null {
    return this.socket
  }
}

// Export singleton instance
export default new SocketService()
